import type { Asset, Transaction, KPI, AuditEntry, Attachment } from '@/types/asset';
import type { Project, DepreciationBatch } from '@/types/depreciation';
import type { Location, Category, Equipment, DisposalReason } from '@/types/settings';
import type { DisposalPreviewRequest, DisposalPreviewResponse, DisposalSubmitRequest, DisposalSubmitResponse } from '@/types/disposal';
import { mockAssets, mockTransactions, mockKPIs, mockAuditLog } from '@/lib/mock-data';
import { mockProjects, mockBatch } from '@/lib/mock-depreciation-data';
import { mockLocations, mockCategories, mockEquipment, mockDisposalReasons } from '@/lib/mock-settings-data';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import { generateAlertsForAsset, calculateAlertSummary } from './alerts-service';

// Helper function to get current data source mode from localStorage
const getDataSource = (): 'demo' | 'api' => {
  const mode = localStorage.getItem('app-mode');
  return mode === 'live' ? 'api' : 'demo';
};

class DataService {
  private authHeader?: string;

  constructor() {
  }

  private getApiBaseUrl(): string {
    const baseUrl = getApiUrl() || import.meta.env.VITE_API_BASE_URL || getBackendUrl();

    try {
      const url = new URL(baseUrl);
      if (url.username || url.password) {
        const credentials = btoa(`${url.username}:${url.password}`);
        this.authHeader = `Basic ${credentials}`;
        url.username = '';
        url.password = '';
        return url.toString().replace(/\/$/, '');
      }
      return baseUrl.replace(/\/$/, '');
    } catch (e) {
      return baseUrl.replace(/\/$/, '');
    }
  }

  private async fetchAPI(endpoint: string, options?: RequestInit) {
    const apiBaseUrl = this.getApiBaseUrl();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    const instanceId = authConfig.instanceId || 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        let errorMessage = `API error: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
        }
        throw new Error(errorMessage);
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to API at ${apiBaseUrl}. Please ensure the backend server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  async getAssets(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    category?: string;
    location?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): Promise<{ data: Asset[]; total: number; page: number; pageSize: number; totalPages: number }> {
    if (getDataSource() === 'demo') {
      let filteredAssets = [...mockAssets];

      if (filters?.status) {
        filteredAssets = filteredAssets.filter(a => a.status === filters.status);
      }
      if (filters?.category) {
        filteredAssets = filteredAssets.filter(a => a.category === filters.category);
      }
      if (filters?.location) {
        filteredAssets = filteredAssets.filter(a => a.location === filters.location);
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        filteredAssets = filteredAssets.filter(a =>
          a.name.toLowerCase().includes(search) ||
          (a.description && a.description.toLowerCase().includes(search))
        );
      }

      if (filters?.sortBy) {
        const sortBy = filters.sortBy as keyof Asset;
        const order = filters.order || 'asc';
        filteredAssets.sort((a, b) => {
          const aVal = a[sortBy];
          const bVal = b[sortBy];
          if (aVal === undefined || bVal === undefined) return 0;
          if (aVal < bVal) return order === 'asc' ? -1 : 1;
          if (aVal > bVal) return order === 'asc' ? 1 : -1;
          return 0;
        });
      }

      // Enrich assets with alert summaries
      const enrichedAssets = filteredAssets.map(asset => {
        const alerts = generateAlertsForAsset(asset);
        const alertSummary = calculateAlertSummary(alerts);
        return {
          ...asset,
          alertSummary: alertSummary.totalCount > 0 ? alertSummary : undefined,
        };
      });

      const page = filters?.page || 1;
      const pageSize = filters?.pageSize || 50;
      const start = (page - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: enrichedAssets.slice(start, end),
        total: enrichedAssets.length,
        page,
        pageSize,
        totalPages: Math.ceil(enrichedAssets.length / pageSize),
      };
    }

    const queryString = new URLSearchParams(
      Object.entries(filters || {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    return this.fetchAPI(`/api/Assets?${queryString}`);
  }

  async getAsset(id: string): Promise<Asset> {
    if (getDataSource() === 'demo') {
      const asset = mockAssets.find(a => a.id === id);
      if (!asset) throw new Error('Asset not found');
      return asset;
    }
    return this.fetchAPI(`/api/Assets/${id}`);
  }

  async createAsset(asset: Partial<Asset>): Promise<Asset> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create assets in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Assets', {
      method: 'POST',
      body: JSON.stringify(asset),
    });
  }

  async updateAsset(id: string, asset: Partial<Asset>): Promise<Asset> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update assets in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(asset),
    });
  }

  async deleteAsset(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete assets in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Assets/${id}`, { method: 'DELETE' });
  }

  async getAssetAttachments(id: string): Promise<Attachment[]> {
    if (getDataSource() === 'demo') {
      const asset = mockAssets.find(a => a.id === id);
      return asset?.attachments || [];
    }
    return this.fetchAPI(`/api/Assets/${id}/attachments`);
  }

  async getAssetAudit(id: string): Promise<AuditEntry[]> {
    if (getDataSource() === 'demo') {
      return mockAuditLog.filter(entry => entry.assetId === id);
    }
    return this.fetchAPI(`/api/Assets/${id}/audit`);
  }

  async getTransactions(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    type?: string;
    assetId?: string;
  }): Promise<{ data: Transaction[]; total: number; page: number; pageSize: number; totalPages: number }> {
    if (getDataSource() === 'demo') {
      let filteredTransactions = [...mockTransactions];

      if (filters?.status) {
        filteredTransactions = filteredTransactions.filter(t => t.status === filters.status);
      }
      if (filters?.type) {
        filteredTransactions = filteredTransactions.filter(t => t.type === filters.type);
      }
      if (filters?.assetId) {
        filteredTransactions = filteredTransactions.filter(t => t.assetId === filters.assetId);
      }

      const page = filters?.page || 1;
      const pageSize = filters?.pageSize || 50;
      const start = (page - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: filteredTransactions.slice(start, end),
        total: filteredTransactions.length,
        page,
        pageSize,
        totalPages: Math.ceil(filteredTransactions.length / pageSize),
      };
    }

    const queryString = new URLSearchParams(
      Object.entries(filters || {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    return this.fetchAPI(`/api/Transactions?${queryString}`);
  }

  async getTransaction(id: string): Promise<Transaction> {
    if (getDataSource() === 'demo') {
      const transaction = mockTransactions.find(t => t.id === id);
      if (!transaction) throw new Error('Transaction not found');
      return transaction;
    }
    return this.fetchAPI(`/api/Transactions/${id}`);
  }

  async createTransaction(transaction: Partial<Transaction>): Promise<Transaction> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create transactions in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async getDashboardKPIs(): Promise<KPI[]> {
    if (getDataSource() === 'demo') {
      return mockKPIs;
    }
    return this.fetchAPI('/api/Dashboard/kpis');
  }

  async getAssetsDueForReview(): Promise<{
    totalCount: number;
    depreciationCount: number;
    insuranceCount: number;
    warrantyCount: number;
  }> {
    if (getDataSource() === 'demo') {
      const { assetsDueForReviewBreakdown } = await import('@/lib/mock-data');
      return assetsDueForReviewBreakdown;
    }
    return this.fetchAPI('/api/Dashboard/assets-due-for-review');
  }

  async getUpcomingInsuranceRenewals(daysAhead: number = 90): Promise<{
    assetId: string;
    assetCode: string;
    assetName: string;
    renewalDate: string;
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceValue?: number;
  }[]> {
    if (getDataSource() === 'demo') {
      const { mockAssets } = await import('@/lib/mock-data');
      const today = new Date();
      const futureDate = new Date(today.getTime() + daysAhead * 24 * 60 * 60 * 1000);
      
      return mockAssets
        .filter(asset => {
          if (!asset.insuranceExpiryDate) return false;
          const expiryDate = new Date(asset.insuranceExpiryDate);
          return expiryDate >= today && expiryDate <= futureDate;
        })
        .map(asset => ({
          assetId: asset.id,
          assetCode: asset.assetId,
          assetName: asset.name,
          renewalDate: asset.insuranceExpiryDate!,
          insuranceProvider: asset.insuranceProvider,
          insurancePolicyNumber: asset.insurancePolicyNumber,
          insuranceValue: asset.insuranceValue,
        }))
        .sort((a, b) => new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime());
    }
    return this.fetchAPI(`/api/Dashboard/insurance-renewals?daysAhead=${daysAhead}`);
  }

  async getNetBookValueTrend(): Promise<{
    currentValue: number;
    change: number;
    trend: string;
    monthlyData: { month: string; value: number }[];
  }> {
    if (getDataSource() === 'demo') {
      const { netBookValueData } = await import('@/lib/mock-data');
      return {
        currentValue: netBookValueData.currentValue,
        change: 5.2,
        trend: 'up',
        monthlyData: netBookValueData.monthlyData,
      };
    }
    return this.fetchAPI('/api/Dashboard/net-book-value-trend');
  }

  async getDepreciationYTDTrend(): Promise<{
    currentValue: number;
    change: number;
    trend: string;
    monthlyData: { month: string; value: number }[];
  }> {
    if (getDataSource() === 'demo') {
      const { depreciationYTDData } = await import('@/lib/mock-data');
      return {
        currentValue: depreciationYTDData.currentValue,
        change: -2.3,
        trend: 'down',
        monthlyData: depreciationYTDData.monthlyData,
      };
    }
    return this.fetchAPI('/api/Dashboard/depreciation-ytd-trend');
  }

  async getDepreciationProjects(): Promise<Project[]> {
    if (getDataSource() === 'demo') {
      return mockProjects;
    }
    return this.fetchAPI('/api/Depreciation/projects');
  }

  async getDepreciationProject(id: string): Promise<Project> {
    if (getDataSource() === 'demo') {
      const project = mockProjects.find(p => p.id === id);
      if (!project) throw new Error('Project not found');
      return project;
    }
    return this.fetchAPI(`/api/Depreciation/projects/${id}`);
  }

  async getDepreciationBatches(filters?: {
    projectId?: string;
    status?: string;
  }): Promise<DepreciationBatch[]> {
    if (getDataSource() === 'demo') {
      const batches = [mockBatch];
      let filteredBatches = [...batches];

      if (filters?.projectId) {
        filteredBatches = filteredBatches.filter(b => b.projectId === filters.projectId);
      }
      if (filters?.status) {
        filteredBatches = filteredBatches.filter(b => b.status === filters.status);
      }

      return filteredBatches;
    }

    const queryString = new URLSearchParams(
      Object.entries(filters || {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    return this.fetchAPI(`/api/Depreciation/batches?${queryString}`);
  }

  async getDepreciationBatch(id: string): Promise<DepreciationBatch> {
    if (getDataSource() === 'demo') {
      if (mockBatch.id === id) {
        return mockBatch;
      }
      throw new Error('Batch not found');
    }
    return this.fetchAPI(`/api/Depreciation/batches/${id}`);
  }

  async createDepreciationBatch(batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create depreciation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/DepreciationBatches', {
      method: 'POST',
      body: JSON.stringify(batch),
    });
  }

  async updateDepreciationBatch(id: string, batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update depreciation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/DepreciationBatches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batch),
    });
  }

  async getRevaluationStatistics(year?: number): Promise<any> {
    if (getDataSource() === 'demo') {
      return {
        year: year || new Date().getFullYear(),
        totalRevaluationAdjustment: 125400.00,
        totalAssetsRevalued: 23,
        totalBatches: 4,
        pendingBatchCount: 1,
        pendingAdjustment: 15200.00
      };
    }

    const queryParams = new URLSearchParams();
    if (year) {
      queryParams.append('year', year.toString());
    }

    const queryString = queryParams.toString();
    return this.fetchAPI(`/api/RevaluationBatches/statistics${queryString ? `?${queryString}` : ''}`);
  }

  async getRevaluationBatches(filters?: { status?: string }): Promise<any[]> {
    if (getDataSource() === 'demo') {
      return [];
    }

    const queryParams = new URLSearchParams();
    if (filters?.status) {
      queryParams.append('status', filters.status);
    }

    const queryString = queryParams.toString();
    return this.fetchAPI(`/api/RevaluationBatches${queryString ? `?${queryString}` : ''}`);
  }

  async getRevaluationBatch(id: string): Promise<any | null> {
    if (getDataSource() === 'demo') {
      return null;
    }
    return this.fetchAPI(`/api/RevaluationBatches/${id}`);
  }
  async createRevaluationBatch(batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create revaluation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/RevaluationBatches', {
      method: 'POST',
      body: JSON.stringify(batch),
    });
  }

  async updateRevaluationBatch(id: string, batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update revaluation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/RevaluationBatches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batch),
    });
  }

  async deleteRevaluationBatch(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete revaluation batch in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/RevaluationBatches/${id}`, { method: 'DELETE' });
  }


  async getLocations(): Promise<Location[]> {
    if (getDataSource() === 'demo') {
      return mockLocations;
    }
    return this.fetchAPI('/api/Settings/locations');
  }

  async getLocation(id: string): Promise<Location> {
    if (getDataSource() === 'demo') {
      const location = mockLocations.find(l => l.id === id);
      if (!location) throw new Error('Location not found');
      return location;
    }
    return this.fetchAPI(`/api/Settings/locations/${id}`);
  }

  async createLocation(location: Partial<Location>): Promise<Location> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create locations in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/locations', {
      method: 'POST',
      body: JSON.stringify(location),
    });
  }

  async updateLocation(id: string, location: Partial<Location>): Promise<Location> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update locations in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(location),
    });
  }

  async deleteLocation(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete locations in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/locations/${id}`, { method: 'DELETE' });
  }

  async getCategories(): Promise<Category[]> {
    if (getDataSource() === 'demo') {
      return mockCategories;
    }
    return this.fetchAPI('/api/Settings/categories');
  }

  async getCategory(id: string): Promise<Category> {
    if (getDataSource() === 'demo') {
      const category = mockCategories.find(c => c.id === id);
      if (!category) throw new Error('Category not found');
      return category;
    }
    return this.fetchAPI(`/api/Settings/categories/${id}`);
  }

  async createCategory(category: Partial<Category>): Promise<Category> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create categories in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/categories', {
      method: 'POST',
      body: JSON.stringify(category),
    });
  }

  async updateCategory(id: string, category: Partial<Category>): Promise<Category> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update categories in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category),
    });
  }

  async deleteCategory(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete categories in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/categories/${id}`, { method: 'DELETE' });
  }

  async getCategoryDefaults(categoryId: string): Promise<import('@/types/settings').CategoryDefaults> {
    if (getDataSource() === 'demo') {
      // Return mock defaults based on category
      return {
        method: 'Straight Line',
        percentage: 20.0,
        years: 5,
        frequency: 'Monthly',
        nominalAccounts: {
          assetCost: { code: '1200', name: 'Fixed Assets' },
          depreciationBS: { code: '1210', name: 'Accumulated Depreciation' },
          depreciationPL: { code: '6100', name: 'Depreciation Expense' },
          disposals: { code: '5100', name: 'Disposal of Assets' },
        },
      };
    }
    return this.fetchAPI(`/api/Settings/categories/${categoryId}/defaults`);
  }

  async getEquipment(): Promise<Equipment[]> {
    if (getDataSource() === 'demo') {
      return mockEquipment;
    }
    return this.fetchAPI('/api/Settings/equipment');
  }

  async getEquipmentItem(id: string): Promise<Equipment> {
    if (getDataSource() === 'demo') {
      const equipment = mockEquipment.find(e => e.id === id);
      if (!equipment) throw new Error('Equipment not found');
      return equipment;
    }
    return this.fetchAPI(`/api/Settings/equipment/${id}`);
  }

  async createEquipment(equipment: Partial<Equipment>): Promise<Equipment> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create equipment in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/equipment', {
      method: 'POST',
      body: JSON.stringify(equipment),
    });
  }

  async updateEquipment(id: string, equipment: Partial<Equipment>): Promise<Equipment> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update equipment in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/equipment/${id}`, {
      method: 'PUT',
      body: JSON.stringify(equipment),
    });
  }

  async deleteEquipment(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete equipment in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/equipment/${id}`, { method: 'DELETE' });
  }

  async getDisposalReasons(): Promise<DisposalReason[]> {
    if (getDataSource() === 'demo') {
      return mockDisposalReasons;
    }
    return this.fetchAPI('/api/Settings/disposal-reasons');
  }

  async getDisposalReason(id: string): Promise<DisposalReason> {
    if (getDataSource() === 'demo') {
      const reason = mockDisposalReasons.find(r => r.id === id);
      if (!reason) throw new Error('Disposal reason not found');
      return reason;
    }
    return this.fetchAPI(`/api/Settings/disposal-reasons/${id}`);
  }

  async createDisposalReason(reason: Partial<DisposalReason>): Promise<DisposalReason> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create disposal reasons in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/disposal-reasons', {
      method: 'POST',
      body: JSON.stringify(reason),
    });
  }

  async updateDisposalReason(id: string, reason: Partial<DisposalReason>): Promise<DisposalReason> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update disposal reasons in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/disposal-reasons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reason),
    });
  }

  async deleteDisposalReason(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete disposal reasons in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/disposal-reasons/${id}`, { method: 'DELETE' });
  }

  // Addition Batch methods
  async getAdditionBatches(status?: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      return [];
    }
    const endpoint = status ? `/api/AdditionBatches?status=${status}` : '/api/AdditionBatches';
    return this.fetchAPI(endpoint);
  }

  async getAdditionBatch(id: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot get addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/AdditionBatches/${id}`);
  }

  async createAdditionBatch(batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/AdditionBatches', {
      method: 'POST',
      body: JSON.stringify(batch),
    });
  }

  async updateAdditionBatch(id: string, batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/AdditionBatches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batch),
    });
  }

  async deleteAdditionBatch(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete addition batch in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/AdditionBatches/${id}`, { method: 'DELETE' });
  }

  // Disposal methods
  async getDisposalPreview(request: DisposalPreviewRequest): Promise<DisposalPreviewResponse> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot preview disposal in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Disposal/preview', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async submitDisposal(request: DisposalSubmitRequest): Promise<DisposalSubmitResponse> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot submit disposal in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Disposal/submit', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Audit methods
  async getAuditLogs(filter: any): Promise<any> {
    if (getDataSource() === 'demo') {
      // Return mock data for demo mode
      return {
        items: mockAuditLog.slice(0, filter.pageSize || 50),
        totalCount: mockAuditLog.length,
        page: filter.page || 1,
        pageSize: filter.pageSize || 50,
        totalPages: Math.ceil(mockAuditLog.length / (filter.pageSize || 50))
      };
    }

    const params = new URLSearchParams();
    if (filter.assetId) params.append('assetId', filter.assetId);
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.actionType) params.append('actionType', filter.actionType);
    if (filter.user) params.append('user', filter.user);
    if (filter.page) params.append('page', filter.page.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortOrder) params.append('sortOrder', filter.sortOrder);

    return this.fetchAPI(`/api/Audit?${params.toString()}`);
  }

  async getAuditLog(id: string): Promise<any> {
    if (getDataSource() === 'demo') {
      return mockAuditLog.find(log => log.id === id);
    }
    return this.fetchAPI(`/api/Audit/${id}`);
  }

  async getAuditActionTypes(): Promise<string[]> {
    if (getDataSource() === 'demo') {
      return ['Create', 'Edit', 'Delete', 'Move', 'StatusChange', 'ValueChange', 'Disposal', 'Transfer'];
    }
    return this.fetchAPI('/api/Audit/action-types');
  }

  async getAuditUsers(): Promise<string[]> {
    if (getDataSource() === 'demo') {
      return ['Jasmine Williams', 'John Smith', 'Sarah Johnson'];
    }
    return this.fetchAPI('/api/Audit/users');
  }

  async exportAuditLogs(filter: any, format: 'csv' | 'pdf'): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot export audit logs in demo mode. Please switch to API mode.');
    }

    const apiBaseUrl = this.getApiBaseUrl();
    const endpoint = format === 'csv' ? '/api/Audit/export/csv' : '/api/Audit/export/pdf';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    const instanceId = authConfig.instanceId || 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        assetId: filter.assetId,
        startDate: filter.startDate,
        endDate: filter.endDate,
        actionType: filter.actionType,
        user: filter.user,
        format
      }),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'html'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async emailAuditTrail(request: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot email audit trail in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Audit/email', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getFinancialsDatabaseInstances(integrationType: string): Promise<{
    instances: Array<{
      id: string;
      name: string;
      environment?: string;
      connectionStatus: string;
      lastSync?: string;
      description?: string;
    }>;
    integrationType: string;
  }> {
    return this.fetchAPI(`/api/financials/instances`);
  }

  async getIntegrationSettings(): Promise<{
    id: string;
    integrationType: string;
    instanceId?: string;
    instanceName?: string;
    enabled: boolean;
    lastSync?: string;
  } | null> {
    try {
      return await this.fetchAPI(`/api/financials/settings`);
    } catch (error) {
      // Return null if settings not found (404)
      return null;
    }
  }

  async saveIntegrationSettings(settings: {
    integrationType: string;
    instanceId?: string;
    instanceName?: string;
    enabled: boolean;
  }): Promise<{
    id: string;
    integrationType: string;
    instanceId?: string;
    instanceName?: string;
    enabled: boolean;
    lastSync?: string;
  }> {
    return this.fetchAPI(`/api/financials/settings`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  // Financial Operations
  async hasTransactions(assetId: string): Promise<boolean> {
    if (getDataSource() === 'demo') {
      // In demo mode, return false for all assets
      return false;
    }
    const response = await this.fetchAPI(`/api/Assets/${assetId}/has-transactions`);
    return response.hasTransactions;
  }

  async generateAssetCode(): Promise<string> {
    if (getDataSource() === 'demo') {
      // In demo mode, generate a simple code
      const timestamp = Date.now();
      return `AST${(timestamp % 1000000).toString().padStart(6, '0')}`;
    }
    const response = await this.fetchAPI('/api/Assets/generate-code', {
      method: 'POST',
    });
    return response.code;
  }

  async getAssetYearValues(assetId: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      // Return mock year values
      const currentYear = new Date().getFullYear();
      return [{
        year: currentYear.toString(),
        cost: {
          startOfYear: 0,
          additions: 1250.00,
          disposals: 0,
          revaluation: 0,
          endOfYear: 1250.00,
        },
        depreciation: {
          startOfYear: 0,
          charge: 312.50,
          disposals: 0,
          revaluation: 0,
          endOfYear: 312.50,
        },
        netBookValue: {
          startOfYear: 0,
          endOfYear: 937.50,
        },
      }];
    }
    return this.fetchAPI(`/api/Assets/${assetId}/year-values`);
  }

  // System Options
  async getSystemOptions(): Promise<any> {
    if (getDataSource() === 'demo') {
      // Return default system options for demo mode
      return {
        configType: 'STANDARD',
        isMultiEntity: false,
        autoNumbering: {
          enabled: false,
          numberOfChars: 10,
          currentNumber: 1,
        },
        validation: {
          noNegativeBoughtValue: false,
        },
        defaults: {
          baseValueOn: 'remaining',
          servicePeriod: 0,
        },
      };
    }
    return this.fetchAPI('/api/Settings/system-options');
  }

  async updateSystemOptions(options: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update system options in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/system-options', {
      method: 'PUT',
      body: JSON.stringify(options),
    });
  }

  async getSubLedgers(type?: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const mockSubLedgers = [
        { id: '1', code: 'SL001', description: 'Main Sub-Ledger', type: 'N' },
        { id: '2', code: 'SL002', description: 'Secondary Sub-Ledger', type: 'N' },
      ];
      if (type) {
        return mockSubLedgers.filter(sl => sl.type === type);
      }
      return mockSubLedgers;
    }
    const params = type ? `?type=${type}` : '';
    return this.fetchAPI(`/api/Settings/sub-ledgers${params}`);
  }

  async getNominalAccounts(currencyCode?: string, multiEntity?: boolean): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const mockAccounts = [
        { id: '1', code: '1000', name: 'Fixed Assets', doNotUse: false, currencyCode: 'GBP' },
        { id: '2', code: '1100', name: 'Accumulated Depreciation', doNotUse: false, currencyCode: 'GBP' },
        { id: '3', code: '6000', name: 'Depreciation Expense', doNotUse: false, currencyCode: 'GBP' },
        { id: '4', code: '7000', name: 'Disposal of Assets', doNotUse: false, currencyCode: 'GBP' },
      ];
      if (currencyCode) {
        return mockAccounts.filter(a => a.currencyCode === currencyCode);
      }
      return mockAccounts;
    }
    const params = new URLSearchParams();
    if (currencyCode) params.append('currencyCode', currencyCode);
    if (multiEntity !== undefined) params.append('multiEntity', String(multiEntity));
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this.fetchAPI(`/api/Settings/nominal-accounts${queryString}`);
  }

  async getSystemYears(sort?: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const currentYear = new Date().getFullYear();
      const years = [
        {
          id: '1',
          label: `${currentYear - 1}`,
          startDate: new Date(currentYear - 1, 0, 1).toISOString(),
          endDate: new Date(currentYear - 1, 11, 31).toISOString(),
          nominal: currentYear - 1,
        },
        {
          id: '2',
          label: `${currentYear}`,
          startDate: new Date(currentYear, 0, 1).toISOString(),
          endDate: new Date(currentYear, 11, 31).toISOString(),
          nominal: currentYear,
        },
        {
          id: '3',
          label: `${currentYear + 1}`,
          startDate: new Date(currentYear + 1, 0, 1).toISOString(),
          endDate: new Date(currentYear + 1, 11, 31).toISOString(),
          nominal: currentYear + 1,
        },
      ];
      if (sort === 'desc') {
        return years.reverse();
      }
      return years;
    }
    const params = sort ? `?sort=${sort}` : '';
    return this.fetchAPI(`/api/Settings/system-years${params}`);
  }

  async getAssetLookupText(origin: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const mockData: Record<string, any[]> = {
        ASSORT1: [
          { id: '1', origin: 'ASSORT1', text: 'Type A', sortOrder: 1 },
          { id: '2', origin: 'ASSORT1', text: 'Type B', sortOrder: 2 },
          { id: '3', origin: 'ASSORT1', text: 'Type C', sortOrder: 3 },
        ],
        ASSORT2: [
          { id: '4', origin: 'ASSORT2', text: 'Priority High', sortOrder: 1 },
          { id: '5', origin: 'ASSORT2', text: 'Priority Medium', sortOrder: 2 },
          { id: '6', origin: 'ASSORT2', text: 'Priority Low', sortOrder: 3 },
        ],
        ASSORT3: [
          { id: '7', origin: 'ASSORT3', text: 'Department A', sortOrder: 1 },
          { id: '8', origin: 'ASSORT3', text: 'Department B', sortOrder: 2 },
        ],
        ASSORT4: [
          { id: '9', origin: 'ASSORT4', text: 'Status Active', sortOrder: 1 },
          { id: '10', origin: 'ASSORT4', text: 'Status Inactive', sortOrder: 2 },
        ],
        ASSORT5: [
          { id: '11', origin: 'ASSORT5', text: 'Region North', sortOrder: 1 },
          { id: '12', origin: 'ASSORT5', text: 'Region South', sortOrder: 2 },
          { id: '13', origin: 'ASSORT5', text: 'Region East', sortOrder: 3 },
          { id: '14', origin: 'ASSORT5', text: 'Region West', sortOrder: 4 },
        ],
      };
      return mockData[origin.toUpperCase()] || [];
    }
    return this.fetchAPI(`/api/Settings/asset-lookup-text?origin=${origin}`);
  }

  // Asset Options - stored in localStorage
  async getAssetOptions(): Promise<any> {
    // Asset options are stored in localStorage
    const savedOptionsStr = localStorage.getItem('assetOptions');
    if (savedOptionsStr) {
      return JSON.parse(savedOptionsStr);
    }
    
    // Return default options if not found
    return {
      general: {
        assetAutoNumbering: false,
        doNotAllowNegativeBoughtValue: false,
        defaultBaseValueOn: 'remaining',
        servicePeriodDefaults: 0,
      },
      defaultSettings: {
        defaultMethod: 'Straight Line',
        defaultPercentage: 20.0,
        defaultPeriodYears: 5,
        defaultFrequency: 'Monthly',
        doNotDepreciate: false,
        forceTransfersOnChangingCategory: false,
      },
    };
  }
}

export const dataService: any = new DataService();
export const isApiMode = (): boolean => getDataSource() === 'api';
export const isDemoMode = (): boolean => getDataSource() === 'demo';
export const getDataSourceMode = (): 'api' | 'demo' => getDataSource();
